import { Vector3, TransformNode, Color3 } from '@babylonjs/core';
import { TransformPosition, TransformRotation, TransformScale, Color } from '../interfaces';

interface MeshAssemblyInterface {
	readonly uniqueId: number;
	readonly name: string;

	parent: MeshAssembly | null;
	localPosition: TransformPosition;
	localRotation: TransformRotation;
	localScale: TransformScale;
	worldPosition: TransformPosition;
	visibility: number;
	shouldRender: boolean;

	getChildren(): Array<MeshAssembly>;
	isReady(): boolean;
}

export class MeshAssembly implements MeshAssemblyInterface {
	public readonly uniqueId: number;

	private parentMeshAssembly: MeshAssembly | null;
	private childrenVisibility: number;
	private isVisible: boolean;
	private _enableOverlay: boolean;
	private _overlayColor: Color;
	private children = new Set<MeshAssembly>();

	constructor(public readonly transformNode: TransformNode, public readonly name = 'empty') {
		this.uniqueId = transformNode.uniqueId;
	}

	public set parent(parent: MeshAssembly | null) {
		if (this.parentMeshAssembly == parent) {
			return;
		}
		if (this.parentMeshAssembly) {
			this.parentMeshAssembly.children.delete(this);
		}
		this.parentMeshAssembly = parent;
		if (parent && parent !== null && this.parentMeshAssembly) {
			this.parentMeshAssembly.children.add(this);
			this.transformNode.parent = parent.transformNode;
		} else {
			this.transformNode.parent = null;
		}
		this.transformNode.computeWorldMatrix(true);
	}

	public get parent(): MeshAssembly | null {
		return this.parentMeshAssembly;
	}

	public set localPosition(position: TransformPosition) {
		this.setLocalProperty('position', position);
	}

	public get localPosition(): TransformPosition {
		return this.transformNode.position;
	}

	public set localRotation(rotation: TransformRotation) {
		this.setLocalProperty('rotation', rotation);
	}

	public get localRotation(): TransformRotation {
		return this.transformNode.rotation;
	}

	public set localScale(scale: TransformScale) {
		this.setLocalProperty('scaling', scale);
	}

	public get localScale(): TransformScale {
		return this.transformNode.scaling;
	}

	public set worldPosition(position: TransformPosition) {
		const { x, y, z } = this.transformNode.getAbsolutePosition();
		const newPosition = new Vector3(
			this.validate(position.x, x, 0),
			this.validate(position.y, y, 0),
			this.validate(position.z, z, 0)
		);
		this.transformNode.setAbsolutePosition(newPosition);
	}

	public get worldPosition(): TransformPosition {
		return this.transformNode.getAbsolutePosition();
	}

	public set shouldRender(value: boolean) {
		if (this.isVisible !== value) {
			this.isVisible = value;
			this.transformNode.getChildMeshes().forEach((mesh) => (mesh.isVisible = value));
		}
	}

	public get shouldRender(): boolean {
		return this.isVisible;
	}

	public set visibility(value: number) {
		if (this.childrenVisibility !== value) {
			this.childrenVisibility = value;
			this.transformNode
				.getChildMeshes()
				.filter((mesh) => !mesh.isAnInstance)
				.forEach((mesh) => (mesh.visibility = value));
		}
	}

	public get visibility(): number {
		return this.childrenVisibility;
	}

	public set enableOverlay(value: boolean) {
		if (this.enableOverlay !== value) {
			this._enableOverlay = value;
			this.transformNode.getChildMeshes().forEach((mesh) => (mesh.renderOverlay = value));
		}
	}

	public get enableOverlay(): boolean {
		return this._enableOverlay;
	}

	public set overlayColor(value: Color) {
		if (this.overlayColor !== value) {
			this._overlayColor = value;
			const newColor = new Color3(value.r, value.g, value.b);
			const alpha = value.a || value.a == 0 ? value.a : 0.5;
			this.transformNode.getChildMeshes().forEach((mesh) => {
				mesh.overlayColor = newColor;
				mesh.overlayAlpha = alpha;
			});
		}
	}

	public get overlayColor(): Color {
		return this._overlayColor;
	}

	public getDistanceTo(position: TransformPosition): number {
		const vector = new Vector3(position.x, position.y, position.z);
		return Vector3.Distance(this.transformNode.getAbsolutePosition(), vector);
	}

	public translatePosition(x: number, y: number, z: number): void {
		this.transformNode.locallyTranslate(new Vector3(x, y, z));
	}

	public isReady(): boolean {
		return this.transformNode.getChildMeshes().every((mesh) => mesh.isReady);
	}

	public getChildren(): Array<MeshAssembly> {
		return Array.from(this.children);
	}

	public destroy(): void {
		this.parent = null;
		this.transformNode.dispose();
	}

	private setLocalProperty(
		propName: string,
		value: { x?: number; y?: number; z?: number } = {}
	): void {
		const { x, y, z } = this.transformNode[propName];
		this.transformNode[propName] = new Vector3(
			this.validate(value.x, x, 0),
			this.validate(value.y, y, 0),
			this.validate(value.z, z, 0)
		);
	}

	private validate<T>(newValue: T, oldValue: T, defaultValue: T): T {
		if (newValue !== undefined && newValue !== null) {
			return newValue;
		}
		if (oldValue !== undefined && oldValue !== null) {
			return oldValue;
		}
		return defaultValue;
	}
}
